import React, { useState, useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Disc3, Music2, UserCircle2, ArrowRight, Loader2, Sparkles, Calendar, CreditCard } from "lucide-react";
import { usePublicAuth } from "../context/PublicAuthContext";
import Header from "../components/Header";
import Footer from "../components/Footer";

// ── Konfetti ──────────────────────────────────────────────────────────────────
const COLORS = [
  "#e11d48", "#f59e0b", "#10b981", "#3b82f6", "#8b5cf6",
  "#ec4899", "#f97316", "#14b8a6", "#a3e635",
];
const CONFETTI_COUNT = 65;

function Confetti({ active }) {
  const pieces = useMemo(() =>
    Array.from({ length: CONFETTI_COUNT }, (_, i) => ({
      id: i,
      color: COLORS[i % COLORS.length],
      left: `${(i / CONFETTI_COUNT) * 100}%`,
      delay: `${(i % 12) * 0.1}s`,
      duration: `${2.8 + (i % 6) * 0.25}s`,
      w: `${6 + (i % 4) * 2}px`,
      h: `${8 + (i % 3) * 3}px`,
      shape: i % 3,
    }))
  , []);

  if (!active) return null;

  return (
    <>
      <style>{`
        @keyframes confetti-fall {
          0%   { transform: translateY(-30px) rotate(0deg);   opacity: 1; }
          80%  { opacity: 1; }
          100% { transform: translateY(105vh) rotate(780deg); opacity: 0; }
        }
        @keyframes confetti-sway {
          0%,100% { margin-left: 0px; }
          25%     { margin-left: 18px; }
          75%     { margin-left: -14px; }
        }
        .cf {
          position: fixed;
          top: -30px;
          pointer-events: none;
          z-index: 60;
          animation:
            confetti-fall var(--dur) var(--del) ease-in both,
            confetti-sway var(--dur) var(--del) ease-in-out both;
        }
      `}</style>
      {pieces.map(p => (
        <div
          key={p.id}
          className="cf"
          style={{
            left: p.left,
            width:  p.shape === 2 ? p.w : p.w,
            height: p.shape === 2 ? "3px" : p.h,
            backgroundColor: p.color,
            borderRadius: p.shape === 1 ? "50%" : "2px",
            "--dur": p.duration,
            "--del": p.delay,
          }}
        />
      ))}
    </>
  );
}

// ── Animierter Haken ──────────────────────────────────────────────────────────
function AnimatedCheck({ visible }) {
  return (
    <div className="flex items-center justify-center">
      <style>{`
        @keyframes ring-draw {
          from { stroke-dashoffset: 283; }
          to   { stroke-dashoffset: 0; }
        }
        @keyframes check-draw {
          from { stroke-dashoffset: 100; }
          to   { stroke-dashoffset: 0; }
        }
        @keyframes check-scale {
          0%   { transform: scale(0.5); opacity: 0; }
          60%  { transform: scale(1.06); }
          100% { transform: scale(1);   opacity: 1; }
        }
        .chk-wrap  { animation: check-scale 0.55s cubic-bezier(0.34,1.56,0.64,1) forwards; }
        .chk-ring  {
          stroke-dasharray: 283;
          stroke-dashoffset: 283;
          animation: ring-draw 0.75s cubic-bezier(0.4,0,0.2,1) forwards;
          transform-origin: 50px 50px;
          transform: rotate(-90deg);
        }
        .chk-path  {
          stroke-dasharray: 100;
          stroke-dashoffset: 100;
          animation: check-draw 0.45s cubic-bezier(0.4,0,0.2,1) 0.7s forwards;
        }
      `}</style>

      <div className={visible ? "chk-wrap" : "opacity-0"}>
        <svg width="128" height="128" viewBox="0 0 100 100" fill="none">
          {/* Glowing disc */}
          <circle cx="50" cy="50" r="48" fill="rgba(16,185,129,0.07)" />
          <circle cx="50" cy="50" r="44" fill="rgba(16,185,129,0.05)" />

          {/* Animated ring */}
          <circle
            className="chk-ring"
            cx="50" cy="50" r="45"
            stroke="#10b981"
            strokeWidth="2.5"
            strokeLinecap="round"
            fill="none"
          />

          {/* Animated checkmark */}
          <path
            className="chk-path"
            d="M27 51 L43 67 L74 36"
            stroke="#10b981"
            strokeWidth="5"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </svg>
      </div>
    </div>
  );
}

// ── Hauptseite ────────────────────────────────────────────────────────────────
export default function SupporterSuccessPage() {
  const { publicUser, setPublicUser, isPublicAuth, isPublicLoading, publicApi } = usePublicAuth();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");

  const [phase, setPhase] = useState("checking"); // checking | success | error
  const [plan, setPlan] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showCheck, setShowCheck] = useState(false);

  // ── Hintergrund-Verifikation ──────────────────────────────────────────────
  useEffect(() => {
    // Warten bis Auth fertig geladen
    if (isPublicLoading) return;

    // Bereits Supporter → sofort anzeigen
    if (publicUser?.is_supporter) {
      setPlan(publicUser.supporter_plan);
      triggerSuccess();
      return;
    }

    // Nicht eingeloggt → nach kurzem Timeout Error-State zeigen
    if (!isPublicAuth) {
      const t = setTimeout(() => setPhase("error"), 4000);
      return () => clearTimeout(t);
    }

    if (!sessionId) {
      setPhase("error");
      return;
    }

    let attempts = 0;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attempts++;
      try {
        const res = await publicApi("get", `/subscriptions/status/${sessionId}`);
        const data = res.data;
        const isSuccess = data.payment_status === "paid" || data.status === "complete";

        if (isSuccess) {
          // User-Daten neu laden um aktuellen Plan zu erhalten
          try {
            const meRes = await publicApi("get", "/me/me");
            setPublicUser(meRes.data);
            setPlan(meRes.data?.supporter_plan || data.plan || null);
          } catch {
            setPlan(data.plan || null);
          }
          triggerSuccess();
          return;
        }

        if (attempts >= 10) {
          setPhase("error");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (attempts < 3) {
          setTimeout(poll, 3000);
        } else {
          setPhase("error");
        }
      }
    };

    poll();
    return () => { cancelled = true; };
  }, [sessionId, isPublicAuth, isPublicLoading]); // eslint-disable-line

  const triggerSuccess = () => {
    setPhase("success");
    // Haken und Konfetti gestaffelt starten
    setTimeout(() => setShowCheck(true), 100);
    setTimeout(() => setShowConfetti(true), 400);
    // Konfetti nach 5 Sek ausblenden
    setTimeout(() => setShowConfetti(false), 5500);
  };

  const planLabel =
    plan === "yearly"  ? "Jährliches Abo" :
    plan === "monthly" ? "Monatliches Abo" :
    null;

  return (
    <>
      <Helmet>
        <title>Willkommen, Supporter! | HUWND</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <Confetti active={showConfetti} />
      <Header />

      <main className="min-h-screen bg-zinc-950 flex flex-col items-center justify-center px-4 py-20">

        {/* ── Hintergrundglow ── */}
        <div className="pointer-events-none fixed inset-0">
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-emerald-500/5 blur-3xl" />
        </div>

        {/* ── Ladezustand ── */}
        {phase === "checking" && (
          <div className="flex flex-col items-center gap-5 text-center">
            <div className="w-20 h-20 rounded-full bg-zinc-900 border border-white/5 flex items-center justify-center">
              <Loader2 size={32} className="text-zinc-400 animate-spin" />
            </div>
            <div>
              <p className="text-white font-bold text-lg">Zahlung wird bestätigt…</p>
              <p className="text-zinc-500 text-sm mt-1">Einen Moment, bitte.</p>
            </div>
          </div>
        )}

        {/* ── Erfolg ── */}
        {phase === "success" && (
          <div className="relative w-full max-w-md">

            {/* Card */}
            <div className="bg-zinc-900/80 border border-emerald-500/15 rounded-3xl p-8 sm:p-10 text-center shadow-2xl backdrop-blur-sm">

              {/* Animierter Haken */}
              <div className="mb-6">
                <AnimatedCheck visible={showCheck} />
              </div>

              {/* Überschrift */}
              <div className="mb-2">
                <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-3 py-1 mb-4">
                  <Sparkles size={11} /> Supporter
                </span>
              </div>
              <h1 className="text-3xl sm:text-4xl font-black text-white font-heading leading-tight mb-3">
                Danke für deine{" "}
                <span className="bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">
                  Unterstützung!
                </span>
              </h1>
              <p className="text-zinc-400 text-sm sm:text-base leading-relaxed mb-6">
                Du bist jetzt Teil der HUWND-Familie und bekommst Zugang zu allen Supporter-Features.
              </p>

              {/* Plan-Badge */}
              {planLabel && (
                <div className="inline-flex items-center gap-2 bg-zinc-800 border border-white/5 rounded-xl px-4 py-2.5 text-sm text-zinc-300 font-medium mb-6">
                  {plan === "yearly"
                    ? <><Calendar size={15} className="text-amber-400" /> {planLabel} – 20 €/Jahr</>
                    : <><CreditCard size={15} className="text-sky-400" /> {planLabel} – 3 €/Monat</>
                  }
                </div>
              )}

              {/* Supporter-Badge */}
              <div className="flex items-center justify-center gap-2 mb-8">
                <div className="w-10 h-10 rounded-full ring-2 ring-rose-500/60 ring-offset-2 ring-offset-zinc-900 bg-zinc-800 flex items-center justify-center">
                  <Disc3 size={18} className="text-rose-400" />
                </div>
                <span className="text-zinc-300 text-sm">
                  {publicUser?.username
                    ? <><span className="text-white font-semibold">{publicUser.username}</span> ist jetzt Supporter</>
                    : "Du bist jetzt Supporter"
                  }
                </span>
              </div>

              {/* Features-Liste */}
              <div className="grid grid-cols-2 gap-2 mb-8 text-left">
                {[
                  "Releases merken",
                  "Artists folgen",
                  "Hype-Votes",
                  "Supporter-Badge",
                  "Kommentare",
                  "Exkl. Features",
                ].map(f => (
                  <div key={f} className="flex items-center gap-2 text-zinc-400 text-xs">
                    <div className="w-3.5 h-3.5 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center flex-shrink-0">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                    </div>
                    {f}
                  </div>
                ))}
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/profil"
                  data-testid="success-go-profile"
                  className="flex-1 flex items-center justify-center gap-2 bg-white text-zinc-900 font-bold rounded-xl px-5 py-3 text-sm hover:bg-zinc-100 transition-colors"
                >
                  <UserCircle2 size={16} /> Mein Profil
                </Link>
                <Link
                  to="/search"
                  data-testid="success-discover-music"
                  className="flex-1 flex items-center justify-center gap-2 bg-zinc-800 border border-white/5 text-white font-semibold rounded-xl px-5 py-3 text-sm hover:bg-zinc-700 transition-colors"
                >
                  <Music2 size={16} /> Musik entdecken <ArrowRight size={14} />
                </Link>
              </div>
            </div>

            {/* Kleiner Hinweis */}
            <p className="text-center text-zinc-600 text-xs mt-5">
              Du erhältst eine Bestätigungs-E-Mail von Stripe.
            </p>
          </div>
        )}

        {/* ── Fehlerfall / Timeout ── */}
        {phase === "error" && (
          <div className="w-full max-w-md text-center">
            <div className="bg-zinc-900 border border-white/5 rounded-3xl p-8">
              <div className="w-16 h-16 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mx-auto mb-5">
                <Sparkles size={28} className="text-amber-400" />
              </div>
              <h2 className="text-xl font-black text-white mb-2">Zahlung wird verarbeitet</h2>
              <p className="text-zinc-400 text-sm leading-relaxed mb-6">
                Deine Zahlung war erfolgreich – wir richten deinen Account gerade ein.
                Das kann einen Moment dauern. Aktualisiere in Kürze dein Profil oder
                nutze die manuelle Überprüfung.
              </p>
              <div className="flex flex-col gap-3">
                <Link
                  to="/profil"
                  className="flex items-center justify-center gap-2 bg-white text-zinc-900 font-bold rounded-xl px-5 py-3 text-sm hover:bg-zinc-100 transition-colors"
                >
                  Zum Profil
                </Link>
                <Link
                  to="/supporter"
                  className="text-zinc-500 text-xs hover:text-zinc-300 transition-colors"
                >
                  Zahlung erneut prüfen
                </Link>
              </div>
            </div>
          </div>
        )}

      </main>

      <Footer />
    </>
  );
}
